import {
  CACHE_HEADER_ENABLED,
  CACHE_HEADER_STALE_TIME,
  listenForCacheUpdate,
} from "@sinch/cache-helpers";
import { useCallback, useEffect, useState } from "react";
import { apiUrls, formatters } from "..";
import { safeRetrieveValue } from "../../helpers/safeRetrieveValue";
import { useLoginToken } from "../../hooks";
import type { ShellPartner } from "../types";
import { useUser } from "./useUser";

type UpdatePartnerFunction = (
  requestOption?: RequestInit
) => Promise<ShellPartner | undefined>;

export const usePartner = (
  shouldFetchInit = true
): [ShellPartner | undefined, UpdatePartnerFunction] => {
  const [user] = useUser(undefined, shouldFetchInit);
  const token = useLoginToken()?.token;

  const businessId = safeRetrieveValue(
    user?.settings || {},
    "businessId",
    (x): x is string | null => x === null || typeof x === "string"
  );

  const [partner, setPartner] = useState<ShellPartner | undefined>();

  const handlePartnerResponse = async (res: Response) => {
    if (!res.ok) {
      console.error(`Failed to fetch partner info: ${await res.text()}`);
      return;
    }
    const platformUser = await res.json();
    const shellUser = formatters.toShellPartner(platformUser);
    setPartner(shellUser);
    return shellUser;
  };

  const fetchPartner = useCallback(async () => {
    try {
      if (!businessId) {
        throw new Error("businessId is not set");
      }
      const partnerResponse = await window.fetch(
        `${apiUrls.partnerUrl}/${businessId}`,
        {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            [CACHE_HEADER_ENABLED]: "true",
            [CACHE_HEADER_STALE_TIME]: "10000",
          },
        }
      );

      return handlePartnerResponse(partnerResponse);
    } catch (error) {
      console.error(`Failed to fetch partner info: ${error}`);
      return;
    }
  }, [businessId, token]);

  useEffect(() => {
    if (token && shouldFetchInit && businessId) {
      fetchPartner();
    }
  }, [shouldFetchInit, fetchPartner, token, businessId]);

  useEffect(
    () => listenForCacheUpdate(new URL(apiUrls.userUrl), handlePartnerResponse),
    []
  );

  return [partner, fetchPartner];
};
